import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { keyBy } from 'lodash';

import { Button, Divider, Flex, Tag, Text } from '@chakra-ui/react';
import { TState, useActions } from '../../redux/store';
import { addTabKPI, deleteTabKPI, updateTabKPI } from '../../api/tabsApi';
import useForm, { TField } from '../form/Form';
import { allIcons, NormalIcon } from '../utils/Icon';
import DragDropList from '../dragAndDrop/DragDropList';
import { isEmpty } from '../../helpers';

export type TKpi = {
  _id?: string;
  field: string;
  type: string;
  value?: string;
  values?: string[];
};

export type TNewKpi = TKpi & {
  tabId: string;
};

const optionEnum: Record<string, any> = {
  avg: 'Moyenne',
  aggregate: 'Addition',
  group: 'Regroupement',
};

const EditKPIs = () => {
  const currentTab = useSelector((state: TState) => state.currentTab);
  const dataInputs = useSelector((state: TState) => state.dataInputs);

  const dataInputsByName = keyBy(dataInputs, 'name_id');

  const { getTabs } = useActions();

  const inputs: TField[] = [
    {
      label: 'Indicateur sur le champ',
      name: 'field',
      type: 'select',
      options: dataInputs.reduce(
        (
          prev: {
            label: string;
            value: string;
          }[],
          dataInput,
        ) => {
          if (!dataInput.name_id || /planning_type/.test(dataInput.name_id))
            return prev;

          if (dataInput.type === 'address')
            return [
              ...prev,
              {
                label: `Code postal ${
                  dataInput.label || dataInput.name_id.replaceAll('_', ' ')
                }`,
                value: `${dataInput.name_id}_postal`,
              },
              {
                label: `Ville ${
                  dataInput.label || dataInput.name_id.replaceAll('_', ' ')
                }`,
                value: `${dataInput.name_id}_city`,
              },
              {
                label: `Département ${
                  dataInput.label || dataInput.name_id.replaceAll('_', ' ')
                }`,
                value: `${dataInput.name_id}_dept`,
              },
            ];

          return [
            ...prev,
            {
              label: dataInput.label || dataInput.name_id.replaceAll('_', ' '),
              value: dataInput.name_id,
            },
          ];
        },
        [{ label: 'Nombre de lignes', value: 'nbrows' }],
      ),
    },
    {
      label: "Type d'indicateur",
      name: 'type',
      type: 'select',
      optionsFn: (d) => {
        const inputType = dataInputsByName[d.field]?.type;
        const isNumber = inputType === 'number';

        return [
          { label: 'Moyenne', value: 'avg' },
          !isNumber ? null : { label: 'Addition', value: 'aggregate' },
          isNumber ? null : { label: 'Regroupement', value: 'group' },
        ].filter((f) => f) as { label: string; value: string }[];
      },
      condition: (d) =>
        d.field &&
        d.field !== 'nbrows' &&
        dataInputsByName[d.field]?.type !== 'number',
    },
    {
      label: 'Valeur',
      name: 'value',
      type: 'multiselect',
      placeholder: 'Tous',
      optionsFn: (d) => {
        const input = dataInputsByName[d.field];
        const isSelect = /select/.test(input?.type);

        return isSelect ? (input?.formOptions as any) : [];
      },
      condition: (d) =>
        d.field &&
        d.type &&
        /avg|aggregate/.test(d.type) &&
        /select/.test(dataInputsByName[d.field]?.type),
    },
  ];

  const inputsByName = keyBy(inputs[0].options, 'value');

  const { Form, formData } = useForm(inputs, { noRowData: true });

  const handleAddKPI = async () => {
    if (
      !currentTab?._id ||
      !formData.field ||
      (formData.field !== 'nbrows' &&
        dataInputsByName[formData.field]?.type !== 'number' &&
        !formData.type)
    )
      return;

    const newKPI: TNewKpi = {
      tabId: currentTab._id,
      field: formData.field,
      type: formData.type,
    };

    if (!isEmpty(formData.value)) {
      if (formData.value.length === 1) {
        newKPI.value = formData.value[0];
      } else {
        newKPI.values = formData.value;
      }
    }

    formData.field = undefined;
    formData.type = undefined;
    formData.value = undefined;

    await addTabKPI(newKPI);
    getTabs();
  };

  const handleOrderChange = async (newOrder: TKpi[]) => {
    if (!currentTab?._id) return;
    await updateTabKPI({ tabId: currentTab._id, KPIs: newOrder });
    getTabs();
  };

  const handleDeleteKPI = async (id: string) => {
    if (!currentTab?._id || !id) return;

    await deleteTabKPI(currentTab._id, id);
    await getTabs();
  };

  useEffect(() => {
    if (
      !/avg|aggregate/.test(formData.type) ||
      !/select/.test(dataInputsByName[formData.field]?.type)
    ) {
      formData.value = undefined;
    }
  }, [formData]);

  return (
    <>
      <Text>Ajouter un indicateur</Text>
      <Flex gap={2} alignItems='flex-end'>
        {Form}
        <Button w='120px' onClick={handleAddKPI}>
          Ajouter
        </Button>
      </Flex>
      <Divider my={3} />
      <DragDropList
        containerStyle={{ flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}
        listItem={currentTab?.KPIs || []}
        List={({ item }) => (
          <Tag pr={1}>
            {inputsByName[item.field]?.label}
            {optionEnum[item.type] ? ` | ${optionEnum[item.type]}` : ''}
            {item.value
              ? ` | ${
                  (item.field &&
                    dataInputsByName?.[item.field]?.options?.[
                      Number(item.value)
                    ]) ||
                  item.value
                }`
              : item.values
              ? ` | ${
                  item.field &&
                  item.values
                    .map(
                      (val) =>
                        dataInputsByName?.[item.field]?.options?.[Number(val)],
                    )
                    .join(' - ')
                }`
              : ''}
            <NormalIcon
              as={allIcons.HiMiniXMark}
              boxSize='20px'
              onClick={() => handleDeleteKPI(item._id!)}
            />
          </Tag>
        )}
        type={'KPIs'}
        onDrop={handleOrderChange}
      />
    </>
  );
};

export default EditKPIs;
